import React from 'react';

export type AiSnapPhase = 'preparing' | 'recognizing';

interface AiSnapScanningOverlayProps {
  previewUrl: string | null;
  phase: AiSnapPhase;
  elapsedSeconds: number;
}

const getProgressCopy = (phase: AiSnapPhase, elapsedSeconds: number) => {
  if (phase === 'preparing') {
    return {
      stage: 0,
      title: '正在优化照片',
      detail: '调整图片方向和尺寸，让 AI 看得更清楚',
    };
  }
  if (elapsedSeconds < 12) {
    return {
      stage: 1,
      title: 'AI 正在查看这餐',
      detail: '正在辨认食物种类、数量和大致份量',
    };
  }
  if (elapsedSeconds < 30) {
    return {
      stage: 2,
      title: '正在估算营养成分',
      detail: '计算热量、蛋白质、脂肪和碳水等数据',
    };
  }
  return {
    stage: 2,
    title: 'AI 仍在认真分析',
    detail: '多菜品或网络较慢时会久一些，结果完成后将自动打开',
  };
};

export const AiSnapScanningOverlay: React.FC<AiSnapScanningOverlayProps> = ({
  previewUrl,
  phase,
  elapsedSeconds,
}) => {
  const progress = getProgressCopy(phase, elapsedSeconds);
  const longWait = phase === 'recognizing' && elapsedSeconds >= 45;

  return (
    <div
      className="as-scan-overlay"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-label="AI 正在识别饮食照片"
    >
      <div className="as-scan-card">
        <div className="as-scan-heading">
          <span className="as-scan-live"><i />AI 饮食识别中</span>
          <span className="as-scan-time">已等待 {elapsedSeconds} 秒</span>
        </div>

        <div className="as-scan-frame">
          {previewUrl ? (
            <img src={previewUrl} alt="正在识别的饮食照片" className="as-scan-photo" />
          ) : (
            <div className="as-scan-placeholder" aria-hidden="true">📷</div>
          )}
          <div className="as-scan-shade" />
          <div className="as-scan-beam" />
          <div className="as-scan-corners" aria-hidden="true">
            <span className="tl" /><span className="tr" />
            <span className="bl" /><span className="br" />
          </div>
          <div className="as-scan-focus" aria-hidden="true"><span /><span /><span /></div>
        </div>

        <div className="as-scan-copy" aria-live="polite">
          <strong>{progress.title}<span className="as-scan-ellipsis" aria-hidden="true">...</span></strong>
          <p>{progress.detail}</p>
        </div>

        <div className="as-scan-steps" aria-label="识别进度">
          {['优化图片', '识别食物', '生成营养结果'].map((label, index) => (
            <React.Fragment key={label}>
              {index > 0 && <span className={`as-scan-step-line ${progress.stage >= index ? 'filled' : ''}`} />}
              <div className={`as-scan-step ${progress.stage === index ? 'active' : ''} ${progress.stage > index ? 'done' : ''}`}>
                <span className="as-scan-step-dot">{progress.stage > index ? '✓' : index + 1}</span>
                <small>{label}</small>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className={`as-scan-tip ${longWait ? 'long-wait' : ''}`}>
          <span aria-hidden="true">{longWait ? '⏳' : '💡'}</span>
          <p>{longWait
            ? '仍在等待 AI 返回，系统会自动处理超时，请不要重复提交。'
            : '请保持页面开启。通常需要 10–30 秒，完成后会自动显示结果。'}</p>
        </div>
      </div>

      <style>{`
        .as-scan-overlay {
          position: fixed;
          inset: 0;
          z-index: 2400;
          display: grid;
          place-items: center;
          box-sizing: border-box;
          padding: max(18px, env(safe-area-inset-top, 0px)) 18px max(18px, env(safe-area-inset-bottom, 0px));
          overflow-y: auto;
          color: #f8fafc;
          background:
            radial-gradient(circle at 50% 30%, rgba(76, 29, 149, 0.34), transparent 46%),
            rgba(2, 6, 23, 0.96);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          animation: asScanFadeIn 180ms ease-out;
        }
        .as-scan-card {
          width: min(440px, 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
        }
        .as-scan-heading {
          width: min(82vw, 360px, 48vh);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: rgba(226, 232, 240, 0.72);
          font-size: 12px;
          font-weight: 750;
        }
        .as-scan-live { display: inline-flex; align-items: center; gap: 7px; }
        .as-scan-live i {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #22d3ee;
          box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.5);
          animation: asScanPulse 1.8s ease-out infinite;
        }
        .as-scan-time { font-variant-numeric: tabular-nums; }
        .as-scan-frame {
          position: relative;
          width: min(82vw, 360px, 48vh);
          aspect-ratio: 4 / 5;
          flex: 0 0 auto;
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(103, 232, 249, 0.25);
          background: linear-gradient(145deg, #111827, #020617);
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.52), 0 0 42px rgba(139, 92, 246, 0.16);
        }
        .as-scan-photo {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
          background: #020617;
        }
        .as-scan-placeholder {
          width: 100%;
          height: 100%;
          display: grid;
          place-items: center;
          font-size: 64px;
          opacity: 0.6;
        }
        .as-scan-shade {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(34,211,238,0.04), transparent 40%, rgba(139,92,246,0.08));
        }
        .as-scan-beam {
          position: absolute;
          z-index: 2;
          left: 4%;
          right: 4%;
          top: 3%;
          height: 3px;
          border-radius: 999px;
          background: linear-gradient(90deg, transparent, #67e8f9 18%, #fff 50%, #a78bfa 82%, transparent);
          box-shadow: 0 0 12px 3px rgba(34,211,238,0.7), 0 0 42px 14px rgba(139,92,246,0.28);
          animation: asScanBeam 2.4s cubic-bezier(.45, 0, .55, 1) infinite;
        }
        .as-scan-corners { position: absolute; z-index: 3; inset: 13px; pointer-events: none; }
        .as-scan-corners span {
          position: absolute;
          width: 30px;
          height: 30px;
          border: 0 solid #67e8f9;
          filter: drop-shadow(0 0 5px rgba(34,211,238,0.75));
        }
        .as-scan-corners .tl { top: 0; left: 0; border-top-width: 3px; border-left-width: 3px; border-radius: 9px 0 0; }
        .as-scan-corners .tr { top: 0; right: 0; border-top-width: 3px; border-right-width: 3px; border-radius: 0 9px 0 0; }
        .as-scan-corners .bl { bottom: 0; left: 0; border-bottom-width: 3px; border-left-width: 3px; border-radius: 0 0 0 9px; }
        .as-scan-corners .br { right: 0; bottom: 0; border-right-width: 3px; border-bottom-width: 3px; border-radius: 0 0 9px; }
        .as-scan-focus {
          position: absolute;
          z-index: 3;
          right: 17px;
          bottom: 17px;
          display: flex;
          gap: 5px;
          padding: 7px 9px;
          border-radius: 999px;
          background: rgba(2, 6, 23, 0.62);
          backdrop-filter: blur(5px);
        }
        .as-scan-focus span {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #67e8f9;
          animation: asScanDot 1.2s ease-in-out infinite;
        }
        .as-scan-focus span:nth-child(2) { animation-delay: 160ms; }
        .as-scan-focus span:nth-child(3) { animation-delay: 320ms; }
        .as-scan-copy { min-height: 58px; text-align: center; }
        .as-scan-copy strong { display: block; color: #fff; font-size: 19px; letter-spacing: 0.1px; }
        .as-scan-copy p { margin: 7px auto 0; max-width: 350px; color: rgba(203, 213, 225, 0.7); font-size: 12px; line-height: 1.5; }
        .as-scan-ellipsis { display: inline-block; width: 16px; overflow: hidden; vertical-align: bottom; animation: asScanEllipsis 1.4s steps(4, end) infinite; }
        .as-scan-steps {
          width: min(350px, 92%);
          display: grid;
          grid-template-columns: auto 1fr auto 1fr auto;
          align-items: start;
        }
        .as-scan-step { display: grid; justify-items: center; gap: 5px; color: rgba(148,163,184,0.55); }
        .as-scan-step-dot {
          width: 24px;
          height: 24px;
          display: grid;
          place-items: center;
          box-sizing: border-box;
          border: 1px solid rgba(148,163,184,0.28);
          border-radius: 50%;
          background: rgba(15,23,42,0.9);
          font-size: 10px;
          font-weight: 900;
        }
        .as-scan-step small { max-width: 72px; font-size: 10px; font-weight: 750; white-space: nowrap; }
        .as-scan-step.active { color: #a5f3fc; }
        .as-scan-step.active .as-scan-step-dot { border-color: #22d3ee; color: #fff; background: rgba(8,145,178,0.35); box-shadow: 0 0 16px rgba(34,211,238,0.3); animation: asScanStep 1.5s ease-in-out infinite; }
        .as-scan-step.done { color: #6ee7b7; }
        .as-scan-step.done .as-scan-step-dot { border-color: #34d399; color: #052e2b; background: #6ee7b7; }
        .as-scan-step-line { height: 1px; margin-top: 12px; background: rgba(148,163,184,0.2); }
        .as-scan-step-line.filled { background: linear-gradient(90deg, #34d399, #22d3ee); }
        .as-scan-tip {
          width: min(380px, 100%);
          display: flex;
          align-items: flex-start;
          gap: 9px;
          box-sizing: border-box;
          padding: 10px 12px;
          border: 1px solid rgba(56,189,248,0.14);
          border-radius: 12px;
          background: rgba(14,165,233,0.07);
        }
        .as-scan-tip span { flex: 0 0 auto; font-size: 13px; }
        .as-scan-tip p { margin: 0; color: rgba(186,230,253,0.72); font-size: 11px; line-height: 1.5; }
        .as-scan-tip.long-wait { border-color: rgba(251,191,36,0.18); background: rgba(245,158,11,0.07); }
        .as-scan-tip.long-wait p { color: rgba(254,240,138,0.75); }
        @keyframes asScanFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes asScanBeam { 0%, 100% { top: 3%; opacity: .65; } 50% { top: calc(97% - 3px); opacity: 1; } }
        @keyframes asScanPulse { 70%, 100% { box-shadow: 0 0 0 8px rgba(34,211,238,0); } }
        @keyframes asScanDot { 0%, 70%, 100% { opacity: .35; transform: translateY(0); } 35% { opacity: 1; transform: translateY(-3px); } }
        @keyframes asScanStep { 50% { box-shadow: 0 0 22px rgba(34,211,238,0.52); } }
        @keyframes asScanEllipsis { from { width: 0; } to { width: 16px; } }
        @media (max-height: 680px) {
          .as-scan-overlay { place-items: start center; }
          .as-scan-card { gap: 10px; }
          .as-scan-copy { min-height: 48px; }
          .as-scan-tip { padding-block: 8px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .as-scan-overlay, .as-scan-beam, .as-scan-live i, .as-scan-focus span, .as-scan-step.active .as-scan-step-dot, .as-scan-ellipsis { animation: none; }
          .as-scan-beam { top: 50%; }
        }
      `}</style>
    </div>
  );
};
